import React, { useEffect, useState } from 'react';
import { max, min } from '@visx/vendor/d3-array';
import { scaleLinear, scaleUtc } from '@visx/scale';
import { Group } from '@visx/group';
import { LinePath } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { AxisBottom } from '@visx/axis';

import { AnalyticsDailyStats } from 'src/types/analytics';
import { YAxisLabel } from 'src/components/HomePage/AnalyticsSection/YAxisLabel';
import { ScaleLinear, ScaleTime } from '@visx/vendor/d3-scale';
import useResizeObserver from 'src/hooks/useResizeObserver';

export type ChartData = Pick<AnalyticsDailyStats, 'total' | 'date'>;

// Data accessors
const getX = (d: ChartData): Date => d.date;
const getY = (d: ChartData): number => d.total;

const isDateTuple = (tuple: [unknown, unknown]): tuple is [Date, Date] => {
  return tuple.every((d) => d instanceof Date);
};

const ensureDateTuple = (tuple: [unknown, unknown]): [Date, Date] => {
  if (!isDateTuple(tuple)) {
    throw new Error('Invalid date tuple');
  }
  return tuple;
};

export function Chart({ data }: { data: ChartData[] }) {
  const [scale, setScale] = useState<{
    x?: ScaleTime<number, number>;
    y?: ScaleLinear<number, number>;
  }>({});
  const hasData = !data.every((d) => d.total === 0);

  const { ref: sizeRef, dimensions } = useResizeObserver<HTMLDivElement>();

  useEffect(() => {
    const { width, height } = dimensions;
    const handleResize = () => {
      const xScale = scaleUtc({
        domain: ensureDateTuple([min(data, getX) ?? 0, max(data, getX) ?? 0]),
        range: [0, width],
        round: true,
      });
      const yScale = scaleLinear({
        domain: [min(data, getY) ?? 0, max(data, getY) ?? 0],
        // The -2 is just visual nudging. There's probably a better way.
        range: [height - 2, 0],
        round: true,
      });

      setScale({ x: xScale, y: yScale });
    };
    addEventListener('resize', handleResize);
    handleResize();
    return () => removeEventListener('resize', handleResize);
  }, [dimensions, data]);

  return (
    <div className="text-current h-35 w-full">
      <div className="flex pb-5">
        <div className="w-6 pr-4 box-content">
          {hasData && <YAxisLabel data={data} />}
        </div>
        <div className="w-full h-full" ref={sizeRef}>
          {scale.x && scale.y && (
            <svg className="w-full h-full border-y border-y-gray-300 border-solid border-x-0">
              <Group>
                <line
                  x1="0%"
                  x2="100%"
                  y1="50%"
                  y2="50%"
                  className="stroke-gray-300"
                />
                {hasData && (
                  <LinePath<ChartData>
                    // A monotone curve prevents the line from going below the x-axis.
                    curve={curveMonotoneX}
                    data={data}
                    shapeRendering="geometricPrecision"
                    stroke="currentColor"
                    strokeOpacity={1}
                    strokeWidth={1}
                    x={(d) => {
                      if (scale.x) {
                        return scale.x(getX(d));
                      } else {
                        return 0;
                      }
                    }}
                    y={(d) => {
                      if (scale.y) {
                        return scale.y(getY(d));
                      } else {
                        return 0;
                      }
                    }}
                  />
                )}
              </Group>
            </svg>
          )}
        </div>
      </div>
      <svg className="w-full h-5 text-gray-500 pl-10 overflow-visible">
        {scale.x && (
          <AxisBottom
            axisClassName="fill-current"
            axisLineClassName="stroke-current"
            hideTicks
            hideAxisLine
            // Go with a low number for the empty state, we can expand
            // it when real data is present.
            numTicks={4}
            scale={scale.x}
            tickComponent={({ x, formattedValue }) => (
              <text
                className="fill-current"
                x={x}
                y={6} // No idea why, it just looks right this way.
                dy=".33em"
                fontSize={13}
                textAnchor="middle"
              >
                {formattedValue}
              </text>
            )}
          />
        )}
      </svg>
    </div>
  );
}
